import { useCallback, useEffect, useState } from 'react';
import {
  ChiTietHoaDonDichVuTraLaiGiamGia,
  ChiTietHoaDonDichVuTraLaiGiamGiaInput,
  HoaDonDichVuTraLaiGiamGia,
  HoaDonDichVuTraLaiGiamGiaInput,
  HoaDonDichVuTraLaiGiamGiaResponse,
  ThongTinThanhToanHoaDonDichVuTraLaiGiamGia
} from '@/types/schemas/hoa-don-dich-vu-tra-lai-giam-gia.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseHoaDonDichVuTraLaiGiamGiaReturn {
  hoaDonDichVuTraLaiGiamGias: HoaDonDichVuTraLaiGiamGia[];
  isLoading: boolean;
  addHoaDonDichVuTraLaiGiamGia: (newHoaDonDichVuTraLaiGiamGia: HoaDonDichVuTraLaiGiamGiaInput) => Promise<void>;
  updateHoaDonDichVuTraLaiGiamGia: (
    uuid: string,
    updatedHoaDonDichVuTraLaiGiamGia: HoaDonDichVuTraLaiGiamGiaInput
  ) => Promise<void>;
  deleteHoaDonDichVuTraLaiGiamGia: (uuid: string) => Promise<void>;
  refreshHoaDonDichVuTraLaiGiamGias: () => Promise<void>;
  getHoaDonDichVuTraLaiGiamGiaById: (uuid: string) => Promise<HoaDonDichVuTraLaiGiamGia | null>;
  getHoaDonDichVuTraLaiGiamGiaByCode: (invoiceCode: string) => Promise<HoaDonDichVuTraLaiGiamGia | null>;
  getHoaDonDichVuTraLaiGiamGiaByCustomer: (customerId: string) => Promise<HoaDonDichVuTraLaiGiamGia[]>;
  getHoaDonDichVuTraLaiGiamGiaByDateRange: (startDate: string, endDate: string) => Promise<HoaDonDichVuTraLaiGiamGia[]>;
  getHoaDonDichVuTraLaiGiamGiaByStatus: (status: string) => Promise<HoaDonDichVuTraLaiGiamGia[]>;
  filterHoaDonDichVuTraLaiGiamGias: (filters: Record<string, any>) => Promise<HoaDonDichVuTraLaiGiamGia[]>;

  // Chi tiet methods
  addChiTietHoaDon: (hoaDonUuid: string, chiTiet: ChiTietHoaDonDichVuTraLaiGiamGiaInput) => Promise<void>;
  updateChiTietHoaDon: (
    hoaDonUuid: string,
    chiTietUuid: string,
    chiTiet: ChiTietHoaDonDichVuTraLaiGiamGiaInput
  ) => Promise<void>;
  deleteChiTietHoaDon: (hoaDonUuid: string, chiTietUuid: string) => Promise<void>;
  getChiTietHoaDon: (hoaDonUuid: string) => Promise<ChiTietHoaDonDichVuTraLaiGiamGia[]>;

  // Thanh toan methods
  addThongTinThanhToan: (hoaDonUuid: string, thanhToan: ThongTinThanhToanHoaDonDichVuTraLaiGiamGia) => Promise<void>;
  updateThongTinThanhToan: (
    hoaDonUuid: string,
    thanhToanUuid: string,
    thanhToan: ThongTinThanhToanHoaDonDichVuTraLaiGiamGia
  ) => Promise<void>;
  deleteThongTinThanhToan: (hoaDonUuid: string, thanhToanUuid: string) => Promise<void>;
  getThongTinThanhToan: (hoaDonUuid: string) => Promise<ThongTinThanhToanHoaDonDichVuTraLaiGiamGia[]>;
}

/**
 * Hook for managing HoaDonDichVuTraLaiGiamGia (Service Return/Discount Invoice) data
 *
 * This hook provides functions to fetch, create, update, and delete service return/discount invoices.
 */
export const useHoaDonDichVuTraLaiGiamGia = (
  initialHoaDonDichVuTraLaiGiamGias: HoaDonDichVuTraLaiGiamGia[] = []
): UseHoaDonDichVuTraLaiGiamGiaReturn => {
  const [hoaDonDichVuTraLaiGiamGias, setHoaDonDichVuTraLaiGiamGias] = useState<HoaDonDichVuTraLaiGiamGia[]>(
    initialHoaDonDichVuTraLaiGiamGias
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchHoaDonDichVuTraLaiGiamGias = useCallback(async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonDichVuTraLaiGiamGiaResponse>(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/`
      );
      setHoaDonDichVuTraLaiGiamGias(response.data.results);
    } catch (error) {
      console.error('Error fetching service return/discount invoices:', error);
    } finally {
      setIsLoading(false);
    }
  }, [entity?.slug]);

  const getHoaDonDichVuTraLaiGiamGiaById = async (uuid: string): Promise<HoaDonDichVuTraLaiGiamGia | null> => {
    if (!entity?.slug) return null;

    try {
      const response = await api.get<HoaDonDichVuTraLaiGiamGia>(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${uuid}/`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching service return/discount invoice with UUID ${uuid}:`, error);
      return null;
    }
  };

  const getHoaDonDichVuTraLaiGiamGiaByCode = async (invoiceCode: string): Promise<HoaDonDichVuTraLaiGiamGia | null> => {
    if (!entity?.slug) return null;

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonDichVuTraLaiGiamGiaResponse>(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/?so_ct=${invoiceCode}`
      );

      if (response.data.results.length > 0) {
        return response.data.results[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching service return/discount invoice by code:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getHoaDonDichVuTraLaiGiamGiaByCustomer = async (customerId: string): Promise<HoaDonDichVuTraLaiGiamGia[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonDichVuTraLaiGiamGiaResponse>(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/?ma_kh=${customerId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching service return/discount invoices by customer:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getHoaDonDichVuTraLaiGiamGiaByDateRange = async (
    startDate: string,
    endDate: string
  ): Promise<HoaDonDichVuTraLaiGiamGia[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonDichVuTraLaiGiamGiaResponse>(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/?start_date=${startDate}&end_date=${endDate}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching service return/discount invoices by date range:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getHoaDonDichVuTraLaiGiamGiaByStatus = async (status: string): Promise<HoaDonDichVuTraLaiGiamGia[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonDichVuTraLaiGiamGiaResponse>(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/?status=${status}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching service return/discount invoices by status:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const filterHoaDonDichVuTraLaiGiamGias = useCallback(
    async (filters: Record<string, any>): Promise<HoaDonDichVuTraLaiGiamGia[]> => {
      if (!entity?.slug) return [];

      try {
        const response = await api.post(
          `/entities/${entity.slug}/erp/ban-hang/hoa-don-dich-vu-tra-lai-giam-gia/filter/`,
          filters
        );

        if (Array.isArray(response.data)) {
          return response.data;
        }

        if (response.data?.results) {
          return response.data.results;
        }

        return [];
      } catch (error) {
        console.error('Error filtering service return/discount invoices via filter API:', error);
        return [];
      }
    },
    [entity?.slug]
  );

  const addHoaDonDichVuTraLaiGiamGia = async (
    newHoaDonDichVuTraLaiGiamGia: HoaDonDichVuTraLaiGiamGiaInput
  ): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.post<HoaDonDichVuTraLaiGiamGia>(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/`,
        newHoaDonDichVuTraLaiGiamGia
      );
      const addedHoaDonDichVuTraLaiGiamGia: HoaDonDichVuTraLaiGiamGia = response.data;
      setHoaDonDichVuTraLaiGiamGias(prev => [...prev, addedHoaDonDichVuTraLaiGiamGia]);
    } catch (error) {
      console.error('Error adding service return/discount invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateHoaDonDichVuTraLaiGiamGia = async (
    uuid: string,
    updatedHoaDonDichVuTraLaiGiamGia: HoaDonDichVuTraLaiGiamGiaInput
  ): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.put<HoaDonDichVuTraLaiGiamGia>(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${uuid}/`,
        updatedHoaDonDichVuTraLaiGiamGia
      );
      const updatedHoaDon: HoaDonDichVuTraLaiGiamGia = response.data;
      setHoaDonDichVuTraLaiGiamGias(prev => prev.map(item => (item.uuid === uuid ? updatedHoaDon : item)));
    } catch (error) {
      console.error('Error updating service return/discount invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteHoaDonDichVuTraLaiGiamGia = async (uuid: string): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${uuid}/`
      );
      setHoaDonDichVuTraLaiGiamGias(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting service return/discount invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Chi tiet methods
  const addChiTietHoaDon = async (
    hoaDonUuid: string,
    chiTiet: ChiTietHoaDonDichVuTraLaiGiamGiaInput
  ): Promise<void> => {
    if (!entity?.slug) return;

    try {
      await api.post(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${hoaDonUuid}/chi-tiet/`,
        chiTiet
      );
      await fetchHoaDonDichVuTraLaiGiamGias(); // Refresh the list
    } catch (error) {
      console.error('Error adding invoice detail:', error);
      throw error;
    }
  };

  const updateChiTietHoaDon = async (
    hoaDonUuid: string,
    chiTietUuid: string,
    chiTiet: ChiTietHoaDonDichVuTraLaiGiamGiaInput
  ): Promise<void> => {
    if (!entity?.slug) return;

    try {
      await api.put(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${hoaDonUuid}/chi-tiet/${chiTietUuid}/`,
        chiTiet
      );
      await fetchHoaDonDichVuTraLaiGiamGias(); // Refresh the list
    } catch (error) {
      console.error('Error updating invoice detail:', error);
      throw error;
    }
  };

  const deleteChiTietHoaDon = async (hoaDonUuid: string, chiTietUuid: string): Promise<void> => {
    if (!entity?.slug) return;

    try {
      await api.delete(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${hoaDonUuid}/chi-tiet/${chiTietUuid}/`
      );
      await fetchHoaDonDichVuTraLaiGiamGias(); // Refresh the list
    } catch (error) {
      console.error('Error deleting invoice detail:', error);
      throw error;
    }
  };

  const getChiTietHoaDon = async (hoaDonUuid: string): Promise<ChiTietHoaDonDichVuTraLaiGiamGia[]> => {
    if (!entity?.slug) return [];

    try {
      const response = await api.get<{ results: ChiTietHoaDonDichVuTraLaiGiamGia[] }>(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${hoaDonUuid}/chi-tiet/`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      return [];
    }
  };

  // Thanh toan methods
  const addThongTinThanhToan = async (
    hoaDonUuid: string,
    thanhToan: ThongTinThanhToanHoaDonDichVuTraLaiGiamGia
  ): Promise<void> => {
    if (!entity?.slug) return;

    try {
      await api.post(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${hoaDonUuid}/thanh-toan/`,
        thanhToan
      );
      await fetchHoaDonDichVuTraLaiGiamGias(); // Refresh the list
    } catch (error) {
      console.error('Error adding payment information:', error);
      throw error;
    }
  };

  const updateThongTinThanhToan = async (
    hoaDonUuid: string,
    thanhToanUuid: string,
    thanhToan: ThongTinThanhToanHoaDonDichVuTraLaiGiamGia
  ): Promise<void> => {
    if (!entity?.slug) return;

    try {
      await api.put(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${hoaDonUuid}/thanh-toan/${thanhToanUuid}/`,
        thanhToan
      );
      await fetchHoaDonDichVuTraLaiGiamGias(); // Refresh the list
    } catch (error) {
      console.error('Error updating payment information:', error);
      throw error;
    }
  };

  const deleteThongTinThanhToan = async (hoaDonUuid: string, thanhToanUuid: string): Promise<void> => {
    if (!entity?.slug) return;

    try {
      await api.delete(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${hoaDonUuid}/thanh-toan/${thanhToanUuid}/`
      );
      await fetchHoaDonDichVuTraLaiGiamGias(); // Refresh the list
    } catch (error) {
      console.error('Error deleting payment information:', error);
      throw error;
    }
  };

  const getThongTinThanhToan = async (hoaDonUuid: string): Promise<ThongTinThanhToanHoaDonDichVuTraLaiGiamGia[]> => {
    if (!entity?.slug) return [];

    try {
      const response = await api.get<{ results: ThongTinThanhToanHoaDonDichVuTraLaiGiamGia[] }>(
        `/entities/${entity.slug}/erp/ban-hang/hoa-don-dieu-chinh-tra-lai/hoa-don-dich-vu-tra-lai-giam-gia/hoa-don-dich-vu-tra-lai-giam-gia/${hoaDonUuid}/thanh-toan/`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching payment information:', error);
      return [];
    }
  };

  const refreshHoaDonDichVuTraLaiGiamGias = async (): Promise<void> => {
    await fetchHoaDonDichVuTraLaiGiamGias();
  };

  useEffect(() => {
    if (entity?.slug) {
      fetchHoaDonDichVuTraLaiGiamGias();
    }
  }, [entity?.slug, fetchHoaDonDichVuTraLaiGiamGias]);

  return {
    hoaDonDichVuTraLaiGiamGias,
    isLoading,
    addHoaDonDichVuTraLaiGiamGia,
    updateHoaDonDichVuTraLaiGiamGia,
    deleteHoaDonDichVuTraLaiGiamGia,
    refreshHoaDonDichVuTraLaiGiamGias,
    getHoaDonDichVuTraLaiGiamGiaById,
    getHoaDonDichVuTraLaiGiamGiaByCode,
    getHoaDonDichVuTraLaiGiamGiaByCustomer,
    getHoaDonDichVuTraLaiGiamGiaByDateRange,
    getHoaDonDichVuTraLaiGiamGiaByStatus,
    filterHoaDonDichVuTraLaiGiamGias,
    // Chi tiet methods
    addChiTietHoaDon,
    updateChiTietHoaDon,
    deleteChiTietHoaDon,
    getChiTietHoaDon,
    // Thanh toan methods
    addThongTinThanhToan,
    updateThongTinThanhToan,
    deleteThongTinThanhToan,
    getThongTinThanhToan
  };
};
